import { parseBahtToSatang } from '@pos/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { PageHeader } from '../../components/PageHeader';
import {
  listBranches,
  listBranchPricing,
  listProducts,
} from '../../data/fetchers';
import { upsertBranchPricing } from '../../data/mutators';
import { fmtAmount } from '../../lib/format';
import { canEditMasterData } from '../../lib/permissions';
import { useSession } from '../../stores/session';

interface PriceChange {
  productId: string;
  branchId: string;
  price: number | null;
}

export function BranchPricingView() {
  const qc = useQueryClient();
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditMasterData(user) : false;
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: listProducts,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const { data: pricing = [] } = useQuery({
    queryKey: ['pricing'],
    queryFn: listBranchPricing,
  });

  // Draft edits keyed by `${productId}:${branchId}` (raw input strings). Empty until edited.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  const baseOf = (id: string) =>
    products.find((p) => p.id === id)?.units[0]?.price ?? 0;
  const overrideOf = (id: string, bid: string) =>
    pricing.find((bp) => bp.productId === id)?.prices[bid];
  const persistedStr = (id: string, bid: string) => {
    const o = overrideOf(id, bid);
    return o != null ? String(o / 100) : '';
  };
  const cellValue = (id: string, bid: string) =>
    drafts[`${id}:${bid}`] ?? persistedStr(id, bid);
  const setDraft = (id: string, bid: string, v: string) => {
    setDrafts((d) => ({ ...d, [`${id}:${bid}`]: v }));
    setSaved(false);
  };

  // Which drafts actually differ from what's persisted → the changes to save.
  const changes = useMemo<PriceChange[]>(() => {
    const out: PriceChange[] = [];
    for (const [key, raw] of Object.entries(drafts)) {
      const [productId, branchId] = key.split(':');
      if (raw === persistedStr(productId, branchId)) continue; // unchanged
      const t = raw.trim();
      if (t !== '' && Number.isNaN(Number(t))) continue; // ignore non-numeric
      out.push({
        productId,
        branchId,
        price: t === '' ? null : parseBahtToSatang(t),
      });
    }
    return out;
  }, [drafts, pricing]);

  const mutation = useMutation({
    mutationFn: async (list: PriceChange[]) => {
      let rows = pricing;
      for (const c of list)
        rows = await upsertBranchPricing(c.productId, c.branchId, c.price);
      return rows;
    },
    onSuccess: (rows) => {
      qc.setQueryData(['pricing'], rows); // update synchronously (no refetch lag)
      void qc.invalidateQueries({ queryKey: ['report'] }); // inventory valuation uses overrides
      setDrafts({});
      setSaved(true);
    },
  });

  return (
    <div className="bo-fade">
      <PageHeader
        title="ราคาแยกสาขา"
        subtitle={
          canEdit
            ? 'แก้ราคาต่อสาขาได้ · เว้นว่าง = ใช้ราคาฐาน · กด “บันทึกราคา” เมื่อแก้เสร็จ'
            : 'ราคาขายต่อหน่วยฐาน แยกตามสาขา'
        }
        actions={
          canEdit ? (
            <div className="flex items-center gap-3">
              {saved && (
                <span className="text-sm-label text-ok font-semibold">
                  บันทึกแล้ว ✓
                </span>
              )}
              {changes.length > 0 && (
                <span className="text-sm-label text-ink-3">
                  แก้ไข {changes.length} ช่อง
                </span>
              )}
              <Btn
                kind="primary"
                disabled={changes.length === 0 || mutation.isPending}
                onClick={() => mutation.mutate(changes)}
              >
                บันทึกราคา
              </Btn>
            </div>
          ) : undefined
        }
      />
      <div className="bo-card overflow-x-auto">
        <table className="bo-table">
          <thead>
            <tr>
              <th>สินค้า</th>
              <th className="bo-th-num">ราคาฐาน</th>
              {branches.map((b) => (
                <th key={b.id} className="bo-th-num">
                  {b.short}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td className="bo-td-num num">{fmtAmount(baseOf(p.id))}</td>
                {branches.map((b) => {
                  const overridden =
                    overrideOf(p.id, b.id) != null ||
                    drafts[`${p.id}:${b.id}`] != null;
                  return (
                    <td key={b.id} className="bo-td-num">
                      {canEdit ? (
                        <input
                          aria-label={`ราคา ${p.id} ${b.id}`}
                          type="number"
                          min={0}
                          step="0.01"
                          value={cellValue(p.id, b.id)}
                          placeholder={fmtAmount(baseOf(p.id))}
                          onChange={(e) => setDraft(p.id, b.id, e.target.value)}
                          className={`num h-8 w-24 px-2 rounded-btn border bg-surface text-body text-right ${overrideOf(p.id, b.id) != null ? 'border-primary text-primary' : 'border-line'}`}
                        />
                      ) : (
                        <span
                          className={`num ${overridden ? 'text-primary font-semibold' : 'text-ink-3'}`}
                        >
                          {fmtAmount(overrideOf(p.id, b.id) ?? baseOf(p.id))}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
